import React, { useRef, useEffect } from 'react';
import { IconButton, Tooltip, HStack, VStack, Text } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import TooltipKeyBox from './TooltipKeyBox';
import { isMobileDevice } from '../utils/deviceDetection';

// Custom filter icon
const FilterIcon: React.FC<{ size?: number; color?: string }> = ({ size = 24, color = 'currentColor' }) => {
  // Match stereo icon stroke width visually
  // Stereo icon uses strokeWidth=2 in 24x24 viewBox
  // Using a slightly thicker stroke width
  const strokeWidth = 22;
  
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 411.5 411.5"
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: 'block' }}
      xmlns="http://www.w3.org/2000/svg"
    >
      <g transform="translate(205.75, 205.75) scale(0.85) translate(-205.75, -205.75)">
        <path d="M214.04,0l17.18,1.57c90.11,10.85,163.77,82.08,177.7,171.76l2.58,24.17v16.5l-1.56,17.19c-10.92,92.51-86.35,167.72-178.71,178.75l-17.18,1.56h-16.5l-17.18-1.56C87.76,398.78,12.75,323.87,1.65,231.18L.08,214.5c.19-5.81-.26-11.7,0-17.5C4.92,90.23,90.87,4.66,197.54,0h16.5ZM391.5,163C374.6,83.36,303.26,22.12,222.28,15.5,60.9,2.31-43.99,183.47,50.17,316.16c1.36,1.91,9.18,12.81,10.65,12.85l72.91-163.84c.48-.47,3.63-2.17,4.08-2.17h253.7ZM143.61,178.06l-71.87,163.2c28.75,29.08,67.91,48.31,108.63,53.67,118.3,15.56,221.31-79.19,216.16-198.2l-2.03-19.23-250.89.56Z"/>
      </g>
    </svg>
  );
};

// Red pulse animation for active filter (similar to stereo mode)
const pulseRed = keyframes`
  0% { 
    color: #E53E3E; /* red.600 */
  }
  25% { 
    color: #E53E3E; /* red.600 - stay red */
  }
  50% { 
    color: #ffa726; /* accent.400 - orange highlight */
  }
  75% { 
    color: #ffa726; /* accent.400 - stay orange */
  }
  100% { 
    color: #E53E3E; /* red.600 */
  }
`;

interface FilterControlButtonProps {
  filterType: 'off' | 'highpass' | 'lowpass' | 'bandpass';
  filterFrequency: number;
  onClick: () => void;
  onToggle?: () => void;
  hasFilterBeenSet?: boolean;
}

const FilterControlButton: React.FC<FilterControlButtonProps> = ({
  filterType,
  filterFrequency,
  onClick,
  onToggle,
  hasFilterBeenSet = false
}) => {
  const isActive = filterType !== 'off';
  const isMobile = isMobileDevice();
  
  // Double-tap/double-click detection
  const lastTapRef = useRef<number>(0);
  const tapTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (tapTimeoutRef.current) {
        clearTimeout(tapTimeoutRef.current);
      }
    };
  }, []);
  
  // Format frequency for display
  const formatFrequency = (freq: number): string => {
    if (freq >= 1000) {
      return `${(freq / 1000).toFixed(1)}kHz`;
    }
    return `${Math.round(freq)}Hz`;
  };

  // Get filter type abbreviation
  const getFilterTypeLabel = (type: string): string => {
    switch (type) {
      case 'highpass':
        return 'HP';
      case 'lowpass':
        return 'LP';
      case 'bandpass':
        return 'BP';
      default:
        return '';
    }
  };

  const filterLabel = isActive 
    ? `${getFilterTypeLabel(filterType)} ${formatFrequency(filterFrequency)}`
    : 'Filter';

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const now = Date.now();
    const timeSinceLastTap = now - lastTapRef.current;
    
    // Check for double-click/tap (works for both mobile and desktop)
    if (timeSinceLastTap < 300 && timeSinceLastTap > 0) {
      // Double click/tap detected
      if (tapTimeoutRef.current) {
        clearTimeout(tapTimeoutRef.current);
        tapTimeoutRef.current = null;
      }
      lastTapRef.current = 0;
      if (onToggle) {
        onToggle();
      }
      return;
    }
    
    // Single click/tap - wait to see if it's a double click/tap
    lastTapRef.current = now;
    tapTimeoutRef.current = setTimeout(() => {
      // Single click/tap confirmed after timeout
      onClick();
      lastTapRef.current = 0;
    }, 300);
  };

  return (
    <Tooltip 
      label={
        <VStack spacing={1} align="flex-start" color="gray.800">
          <HStack spacing={2}>
            <Text>Open filters</Text>
            <TooltipKeyBox>F</TooltipKeyBox>
          </HStack>
          {!isMobile && onToggle && filterType !== 'off' && (
            <HStack spacing={2}>
              <Text>Toggle</Text>
              <TooltipKeyBox>FF</TooltipKeyBox>
              <Text>/ double-click</Text>
            </HStack>
          )}
        </VStack>
      } 
      placement="bottom"
      hasArrow
    >
      <IconButton
        data-testid="filter-control-button"
        aria-label={`Master Filter (Current: ${filterLabel})`}
        icon={<FilterIcon size={24} />}
        onClick={handleClick}
        size="lg"
        borderRadius="full"
        variant="ghost"
        bg="transparent"
        color={isActive ? "red.600" : "gray.400"}
        animation={isActive ? `${pulseRed} 1.5s ease-in-out infinite` : undefined}
        _hover={{
          color: isActive ? "red.500" : "accent.400",
          transform: 'scale(1.05)',
          borderColor: 'transparent'
        }}
        _active={{
          color: isActive ? "red.700" : "accent.500",
          transform: 'scale(0.95)',
          borderColor: 'transparent'
        }}
        _focus={{ 
          boxShadow: "none",
          outline: "none",
        }}
        _focusVisible={{ 
          boxShadow: "none",
          outline: "none",
        }}
      />
    </Tooltip>
  );
};

export default FilterControlButton;

